import axios from 'axios';
import { getBaseUrl } from 'utils/url';

export type SnapshotListStatus =
  | 'ready'
  | 'waiting_on_partner'
  | 'waiting_on_you'
  | 'expiring_soon'
  | 'processing'
  | 'expired'
  | 'failed'
  | string;

export type SnapshotListItem = {
  id: string;
  partnerCompanyName: string;
  partnerCompanyDomain: string | null;
  status: SnapshotListStatus;
  rawStatus?: string;
  overlap: {
    count: number;
    rateMin: number;
    rateMax: number;
    viewerRate?: number;
  } | null;
  invitedAt?: unknown;
  expiresAt?: unknown;
  readyAt?: unknown;
  createdAt?: unknown;
  initiatorCompanyName?: string;
  isInitiator?: boolean;
};

export type SnapshotDetail = {
  id: string;
  status: SnapshotListStatus;
  rawStatus?: string;
  partnerCompanyName: string;
  partnerEmail?: string;
  partnerCompanyDomain: string | null;
  initiatorCompanyName: string;
  initiatorAccountId?: string;
  partnerAccountId?: string | null;
  uploadPrompt?: string | null;
  counts: {
    initiatorCount: number;
    partnerCount: number;
    overlapping: number;
    overlapRate: number;
    overlapRateMin: number;
    overlapRateMax: number;
  } | null;
  playbooks: {
    upsells: number;
    accelerate: number;
    generate: number;
    partnerships: number;
    leadToLead: number;
  } | null;
  createdAt?: unknown;
  readyAt?: unknown;
  expiresAt?: unknown;
  invitedAt?: unknown;
  filesDeletedAt?: unknown;
  isInitiator?: boolean;
  yourDataRemoved?: boolean;
  otherPartyDataRemoved?: boolean;
};

export type SnapshotWithdrawResponse = {
  withdrawn?: boolean;
  yourDataRemoved?: boolean;
  resultsRetained?: boolean;
  fullSnapshotRemoved?: boolean;
};

export type SnapshotOverlap = {
  id: string;
  domain: string;
  initiator: {
    company: string;
    lifecycleStage: string;
    accountOwner?: string | null;
    notes?: string | null;
  };
  partner: {
    company: string;
    lifecycleStage: string;
    accountOwner?: string | null;
    notes?: string | null;
  };
  playbooks: string[];
};

export type SnapshotJoinPreview = {
  snapshotId: string;
  initiatorCompanyName: string;
  partnerCompanyName: string;
  uploadPrompt: string | null;
  status: string;
  joinUrl: string;
};

export type SnapshotCsvFieldId =
  | 'url'
  | 'company'
  | 'lifecycleStage'
  | 'accountOwner'
  | 'notes';

export type SnapshotStageBucketId = 'lead' | 'opportunity' | 'customer';

export type SnapshotCsvPreview = {
  fileName: string | null;
  rowCount: number;
  fields: { id: SnapshotCsvFieldId; label: string; required: boolean }[];
  stageBuckets: { id: SnapshotStageBucketId; label: string }[];
  columns: {
    header: string;
    sample: string | null;
    suggestedField: SnapshotCsvFieldId | null;
  }[];
  unusedColumns: string[];
  suggestedColumnMapping: Partial<Record<SnapshotCsvFieldId, string>>;
  columnMapping: Partial<Record<SnapshotCsvFieldId, string>>;
  stages: {
    value: string;
    count: number;
    suggestedBucket: SnapshotStageBucketId | null;
  }[];
  suggestedStageMapping: Record<string, SnapshotStageBucketId>;
  rowImport: {
    importableCount: number;
    skippedCount: number;
    skipped: string[];
  } | null;
  warnings: string[];
};

export type SnapshotCsvMappings = {
  columnMapping: Record<string, string>;
  stageMapping: Record<string, SnapshotStageBucketId>;
};

const baseUrl = `${getBaseUrl()}/api/snapshots`;

function appendCsvMappings(
  form: FormData,
  mappings?: Partial<SnapshotCsvMappings>,
) {
  if (mappings?.columnMapping && Object.keys(mappings.columnMapping).length > 0) {
    form.append('columnMapping', JSON.stringify(mappings.columnMapping));
  }
  if (mappings?.stageMapping && Object.keys(mappings.stageMapping).length > 0) {
    form.append('stageMapping', JSON.stringify(mappings.stageMapping));
  }
}

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

export async function listSnapshots(token: string) {
  const res = await axios.get<{ snapshots: SnapshotListItem[] }>(baseUrl, {
    headers: authHeaders(token),
  });
  return res.data.snapshots;
}

export async function getSnapshot(token: string, id: string) {
  const res = await axios.get<{ snapshot: SnapshotDetail }>(`${baseUrl}/${id}`, {
    headers: authHeaders(token),
  });
  return res.data.snapshot;
}

export async function previewSnapshotCsv(
  token: string,
  file: File,
  columnMapping?: Record<string, string>,
  stageMapping?: Record<string, SnapshotStageBucketId>,
) {
  const form = new FormData();
  form.append('file', file);
  appendCsvMappings(form, { columnMapping, stageMapping });
  const res = await axios.post<SnapshotCsvPreview>(`${baseUrl}/preview`, form, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function createSnapshot(
  token: string,
  params: {
    partnerCompanyName: string;
    partnerEmail: string;
    partnerCompanyDomain?: string;
    uploadPrompt?: string;
    file: File;
    columnMapping?: Record<string, string>;
    stageMapping?: Record<string, SnapshotStageBucketId>;
  },
) {
  const form = new FormData();
  form.append('partnerCompanyName', params.partnerCompanyName);
  form.append('partnerEmail', params.partnerEmail);
  if (params.partnerCompanyDomain) {
    form.append('partnerCompanyDomain', params.partnerCompanyDomain);
  }
  if (params.uploadPrompt) {
    form.append('uploadPrompt', params.uploadPrompt);
  }
  form.append('file', params.file);
  appendCsvMappings(form, {
    columnMapping: params.columnMapping,
    stageMapping: params.stageMapping,
  });

  const res = await axios.post(baseUrl, form, {
    headers: authHeaders(token),
  });
  return res.data as { snapshotId: string; status: string };
}

export async function uploadPartnerSnapshot(
  token: string,
  snapshotId: string,
  file: File,
  mappings?: SnapshotCsvMappings,
) {
  const form = new FormData();
  form.append('file', file);
  appendCsvMappings(form, mappings);
  const res = await axios.post(`${baseUrl}/${snapshotId}/sides/partner`, form, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getSnapshotOverlaps(
  token: string,
  snapshotId: string,
  playbook?: string,
) {
  const params = playbook ? { playbook } : {};
  const res = await axios.get<{ overlaps: SnapshotOverlap[] }>(
    `${baseUrl}/${snapshotId}/overlaps`,
    { headers: authHeaders(token), params },
  );
  return res.data.overlaps;
}

export async function resendSnapshotInvite(token: string, snapshotId: string) {
  const res = await axios.post(
    `${baseUrl}/${snapshotId}/invite/resend`,
    {},
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function deleteSnapshot(token: string, snapshotId: string) {
  const res = await axios.delete<SnapshotWithdrawResponse>(`${baseUrl}/${snapshotId}`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getJoinPreview(token: string) {
  const res = await axios.get<SnapshotJoinPreview>(
    `${baseUrl}/join/${encodeURIComponent(token)}`,
  );
  return res.data;
}

export async function acceptSnapshotInvite(token: string, inviteToken: string) {
  const res = await axios.post(
    `${baseUrl}/accept-invite`,
    { token: inviteToken },
    { headers: authHeaders(token) },
  );
  return res.data as { snapshotId: string };
}

export async function downloadSnapshotTemplate(token: string) {
  const res = await axios.get(`${baseUrl}/template`, {
    headers: authHeaders(token),
    responseType: 'blob',
  });
  return res.data as Blob;
}
